import React, {
  type ComponentPropsWithoutRef,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';

import { isKey } from '../../../helpers/keyboard';
import { classNames } from '../../../helpers/text';
import { useRefAndForward } from '../../../hooks';
import { Flex } from '../../Flex';
import { Text } from '../../Text';
import { Radio } from '../Radio';

import { useSelectContext } from './context';
import { Styled } from './styles';

type SelectOptionProps<T> = {
  value?: T | null;
  label?: ReactNode;
  isPlaceholder?: boolean;
};

type Props<T> = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & SelectOptionProps<T>;

export const SelectOption = forwardRef<HTMLDivElement, Props<any>>(
  ({ value, label, isPlaceholder, className, ...props }, ref) => {
    const { value: selectedValue, formatLabel, onChange, variant, registerOption } = useSelectContext<any>();
    const nodeRef = useRefAndForward<HTMLDivElement | null>(null, ref);
    const registrationKey = useRef({}).current;

    useEffect(() => {
      if (!registerOption || value === undefined) return;

      return registerOption(registrationKey, { value, isPlaceholder, getNode: () => nodeRef.current });
    }, [registerOption, registrationKey, value, isPlaceholder, nodeRef]);

    const labelFromValue = formatLabel(value);
    const labelFromSelectedValue = formatLabel(selectedValue);
    const hasValue = value !== undefined;
    const isSelected = hasValue && labelFromValue === labelFromSelectedValue;

    const onClick = (event: MouseEvent) => {
      if (isPlaceholder) {
        return event.preventDefault();
      }
      return onChange(value);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isKey('space', 'enter')(event)) {
        event.preventDefault();
        onChange(value);
      }
    };

    return (
      <Styled.DropdownOption
        {...props}
        className={classNames(isSelected && 'focus', className)}
        ref={nodeRef}
        role="option"
        aria-selected={isSelected}
        onKeyDown={onKeyDown}
        onClick={onClick}
        onFocus={e => e.stopPropagation()}
        isPlaceholder={isPlaceholder}
      >
        <Flex.Row space="s">
          {variant === 'radio' && (
            <Styled.RadioContainer>
              <Radio checked={isSelected} />
            </Styled.RadioContainer>
          )}
          <Text variant="body-lighter">{label ?? labelFromValue}</Text>
        </Flex.Row>
      </Styled.DropdownOption>
    );
  },
);
SelectOption.displayName = 'Select.Option';
